import { useCallback } from 'react';

/** A single device GPS fix. `accuracyM` is the reported horizontal accuracy in metres
 *  (the server rejects a fix worse than 100 m); `isMockLocation` is a mobile-only spoof
 *  signal — the browser can't detect faked GPS, so the web always reports false. */
export type DeviceFix = { lat: number; lng: number; accuracyM: number | null; isMockLocation: boolean };

// Friendly, actionable copy per W3C GeolocationPositionError code.
const GEO_ERRORS: Record<number, string> = {
  1: 'Location permission is blocked. Allow location for this site (tap the icon in the address bar) and try again.',
  2: 'Your location is unavailable right now. Move to an open area with a clear view of the sky and try again.',
  3: 'Getting your location timed out. Check that GPS / location is switched on, then try again.',
};

/**
 * Get a FRESH, precise device fix for a location-gated field action (punch in/out, visit
 * start / reached / leave / cancel). This is a HARD GATE: if the device can't produce a fix,
 * it rejects — there is no manual-coordinate fallback for a rep (that path is reserved for an
 * authorised on-behalf user). The fix carries `accuracyM` so the server can reject an imprecise
 * position, and `isMockLocation` (always false on web) for the anti-spoof gate on mobile.
 *
 * `maximumAge: 0` forces a new reading every time so a stale/cached fix can't be replayed;
 * `enableHighAccuracy: true` asks for the GPS-grade position the anti-fraud floor expects.
 */
export function getDeviceFix(): Promise<DeviceFix> {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('This device has no location support — use the mobile app for field actions.'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (p) =>
        resolve({
          lat: p.coords.latitude,
          lng: p.coords.longitude,
          accuracyM: p.coords.accuracy != null ? Math.round(p.coords.accuracy) : null,
          isMockLocation: false,
        }),
      (e: GeolocationPositionError) => reject(new Error(GEO_ERRORS[e?.code] ?? 'Could not get your location. Try again.')),
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 },
    );
  });
}

/** Hook wrapper so components can call `const { resolve } = useDeviceFix()`. */
export function useDeviceFix() {
  const resolve = useCallback(() => getDeviceFix(), []);
  return { resolve };
}
